import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, FileText } from "lucide-react";
import { getAthlete, listTests } from "../lib/api";
import CompareChart from "../components/CompareChart";
import { toast } from "sonner";

function formatDate(s) {
  if (!s) return "—";
  try {
    // Date-only YYYY-MM-DD parsed as LOCAL to avoid the UTC-midnight shift.
    const m = typeof s === "string" && /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    const d = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(s);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return s;
  }
}

export default function CompareTestsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // id -> true
  const [showHR, setShowHR] = useState(false);
  const [displayUnit, setDisplayUnit] = useState("mph");

  useEffect(() => {
    (async () => {
      try {
        const [a, t] = await Promise.all([getAthlete(id), listTests(id)]);
        setAthlete(a);
        setTests(t);
        // default: select the two most recent tests if available
        const initial = {};
        t.slice(0, 2).forEach((x) => (initial[x.id] = true));
        setSelected(initial);
        if (t[0]) setDisplayUnit(t[0].speed_unit || "mph");
      } catch {
        toast.error("Couldn't load athlete");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // selected tests ordered oldest → newest so the newest draws last (and is solid)
  const selectedTests = useMemo(() => {
    return tests
      .filter((t) => selected[t.id])
      .slice()
      .sort((a, b) => (a.test_date || "").localeCompare(b.test_date || ""));
  }, [tests, selected]);

  const toggle = (tid) => setSelected((s) => ({ ...s, [tid]: !s[tid] }));

  const delta = useMemo(() => {
    if (selectedTests.length < 2) return null;
    const oldest = selectedTests[0];
    const newest = selectedTests[selectedTests.length - 1];
    const oLT2 = oldest.lt2?.speed;
    const nLT2 = newest.lt2?.speed;
    if (oLT2 == null || nLT2 == null || oLT2 === "" || nLT2 === "") return null;
    // normalise both to display unit
    const toDisplay = (v, u) =>
      u === displayUnit ? Number(v) : u === "mph" ? Number(v) * 1.609344 : Number(v) / 1.609344;
    const a = toDisplay(oLT2, oldest.speed_unit || "mph");
    const b = toDisplay(nLT2, newest.speed_unit || "mph");
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return { from: a, to: b, delta: b - a };
  }, [selectedTests, displayUnit]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]">
        Loading…
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="compare-tests-page">
      <Link
        to={`/athletes/${id}`}
        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors mb-6"
        data-testid="back-to-athlete"
      >
        <ArrowLeft size={14} /> Back to {athlete?.name || "athlete"}
      </Link>

      <header className="border-b border-[var(--tir-ink)] pb-6 mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-3">
            Compare Tests
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-[var(--tir-ink)] leading-none">
            {athlete?.name} · Progress overlay
          </h1>
          <p className="text-sm text-[var(--tir-muted)] mt-3 max-w-2xl leading-relaxed">
            Pick two or more tests to see the lactate curve shift side-by-side. Older tests are
            dashed; the most recent test is solid and emphasized.
          </p>
        </div>
        <button
          type="button"
          disabled={selectedTests.length < 2}
          onClick={() => {
            const ids = selectedTests.map((t) => t.id).join(",");
            navigate(`/athletes/${id}/compare/report?tests=${ids}`);
          }}
          data-testid="generate-compare-report-btn"
          className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
        >
          <FileText size={14} /> Generate Report
        </button>
      </header>

      {tests.length < 2 ? (
        <div className="border border-dashed border-[var(--tir-border)] py-16 text-center">
          <div className="font-serif-display text-xl text-[var(--tir-ink)]">
            Need at least 2 tests to compare
          </div>
          <div className="text-sm text-[var(--tir-muted)] mt-2">
            Add a follow-up test for {athlete?.name} to see the shift.
          </div>
        </div>
      ) : (
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-5">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)] font-semibold mb-2">
                Tests
              </div>
              <ul className="border border-[var(--tir-border)] bg-white divide-y divide-[var(--tir-border)]">
                {tests.map((t) => {
                  const on = !!selected[t.id];
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => toggle(t.id)}
                        data-testid={`toggle-test-${t.id}`}
                        className={`w-full text-left px-4 py-3 flex items-center justify-between gap-4 transition-colors ${
                          on ? "bg-[var(--tir-ivory)]" : "hover:bg-[var(--tir-ivory)]/50"
                        }`}
                      >
                        <div>
                          <div className="font-serif-display text-lg text-[var(--tir-ink)] leading-tight">
                            {formatDate(t.test_date)}
                          </div>
                          <div className="text-[11px] tracking-[0.18em] uppercase text-[var(--tir-muted)] mt-0.5">
                            {t.protocol} · {t.speed_unit.toUpperCase()}
                            {t.lt2?.speed ? ` · LT2 ${t.lt2.speed}` : ""}
                          </div>
                        </div>
                        <div
                          className={`h-5 w-5 border flex items-center justify-center flex-shrink-0 ${
                            on
                              ? "bg-[var(--tir-burgundy)] border-[var(--tir-burgundy)] text-white"
                              : "border-[var(--tir-border)] bg-white"
                          }`}
                        >
                          {on && <Check size={12} strokeWidth={3} />}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)] font-semibold mb-2">
                Display unit
              </div>
              <div
                className="inline-flex border border-[var(--tir-ink)] h-9 w-full"
                data-testid="compare-unit-toggle"
              >
                {["mph", "kph"].map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setDisplayUnit(u)}
                    data-testid={`compare-unit-${u}`}
                    className={`flex-1 text-xs tracking-[0.22em] uppercase font-semibold transition-colors ${
                      displayUnit === u
                        ? "bg-[var(--tir-ink)] text-white"
                        : "bg-white text-[var(--tir-ink)] hover:bg-[var(--tir-ivory)]"
                    }`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showHR}
                onChange={(e) => setShowHR(e.target.checked)}
                data-testid="toggle-show-hr"
                className="h-4 w-4 accent-[var(--tir-burgundy)]"
              />
              <span className="text-sm text-[var(--tir-ink)]">Overlay heart-rate curves</span>
            </label>

            {delta && (
              <div
                className="border-l-2 border-[var(--tir-burgundy)] bg-white px-4 py-3"
                data-testid="lt2-delta"
              >
                <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
                  LT2 shift
                </div>
                <div className="font-serif-display text-2xl text-[var(--tir-ink)] leading-tight mt-1">
                  {delta.from.toFixed(1)} → {delta.to.toFixed(1)} {displayUnit}
                </div>
                <div
                  className={`text-sm font-mono-tir mt-1 ${
                    delta.delta >= 0 ? "text-[var(--tir-burgundy)]" : "text-[var(--tir-muted)]"
                  }`}
                >
                  {delta.delta >= 0 ? "+" : ""}
                  {delta.delta.toFixed(2)} {displayUnit}{" "}
                  {delta.delta >= 0 ? "faster at threshold" : "slower at threshold"}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-8">
            <div className="border border-[var(--tir-ink)] bg-white p-5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Progress overlay
              </div>
              <div className="font-serif-display text-2xl text-[var(--tir-ink)] mb-4">
                {selectedTests.length < 2
                  ? "Select at least 2 tests"
                  : `${selectedTests.length} tests layered`}
              </div>
              {selectedTests.length >= 1 ? (
                <CompareChart
                  tests={selectedTests}
                  displayUnit={displayUnit}
                  showHR={showHR}
                  height={480}
                />
              ) : (
                <div className="h-[400px] flex items-center justify-center text-[var(--tir-muted)] text-sm">
                  No tests selected.
                </div>
              )}
            </div>
            <p className="text-xs text-[var(--tir-muted)] mt-3 leading-relaxed">
              Values from tests in {["mph", "kph"].find((u) => u !== displayUnit).toUpperCase()}{" "}
              are auto-converted to {displayUnit.toUpperCase()} for comparison.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
